import React, { useEffect, useRef, useState } from 'react'
import { flowResult } from 'mobx'
import { useLocation } from 'react-router-dom'
import { useContentStore, useRetailMediaStore, useRapidDeliveryStore } from './useStore'
import { DefaultContentBody } from 'dc-delivery-sdk-js'
import { setTopSlot, setRowSlots } from '../../pages/category/partials/display-retail-media-slot'

export function usePLPBanners(filterKey: string | null, pageType: string | null, query?: string | null) {
  const {fetchItemByKey, contentById } = useContentStore()
  const { fetchBannerAds } = useRetailMediaStore()
  const {
    isRapidDeliveryBooked,
    isRapidSponsoredProductsHidden,
    rapidDeliveryBlockedSchemas,
    rapidDeliveryRulesLoaded,
  } = useRapidDeliveryStore()
  const {pathname} = useLocation()

  const [topSlot, setPLPTopSlot] = useState<React.ReactNode>(null)
  const [rowSlots, setPLPRowSlots] = useState<Array<DefaultContentBody> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const deliveryKey = useRef('')

  function clearSlotData() {
    setPLPTopSlot(null)
    setPLPRowSlots(null)
    setIsLoading(true)
  }

  useEffect(() => {
    clearSlotData()
    deliveryKey.current = ''
  }, [pathname])

  useEffect(() => {
    async function fetchPLPContent() {

      if (!filterKey || (pageType != 'CATEGORY' && pageType != 'SEARCH')) {
        clearSlotData()
        setIsLoading(false)
        return
      }

      try {

        // Ensures we have a hyphenated and whitespace free delivery key to fetch from Amplience
        const currentKey = filterKey.trim().replace(" ", "-")

        if (currentKey && currentKey != deliveryKey.current) {
          // Update key references to keep track of changes
          deliveryKey.current = currentKey

          // Get current page content
          await flowResult(fetchItemByKey(deliveryKey.current))
          const page = contentById[deliveryKey.current]
          let filterValue
          if (pageType === 'CATEGORY') { 
            filterValue = page?.name 
          } else if (pageType === 'SEARCH') {
            filterValue = query
          } else {
            filterValue = deliveryKey.current
          }

          const retailMediaContent = await flowResult(fetchBannerAds(pageType.toLowerCase(), filterValue))

          const shouldSuppressRetailMedia =
            isRapidDeliveryBooked && (isRapidSponsoredProductsHidden ?? true)
          const blockedSchemas = shouldSuppressRetailMedia ? rapidDeliveryBlockedSchemas : []

          const {topSlot} = setTopSlot(page, retailMediaContent, blockedSchemas)
          setPLPTopSlot(topSlot)
          const rowSlots = setRowSlots(page, retailMediaContent, blockedSchemas)
          setPLPRowSlots(rowSlots)
          setIsLoading(false)
        }
        
        // Reset to null if no delivery key or no search page found
        if (!currentKey || !contentById[currentKey]) {
          setPLPTopSlot(null)
          setPLPRowSlots(null)
          setIsLoading(false)
        }

      } catch (error) {
        console.log('Error during setting PLP banner content', error)
        clearSlotData()
        setIsLoading(false)
      }
    }

    fetchPLPContent()
  }, [
    filterKey,
    pageType,
    query,
    isRapidDeliveryBooked,
    isRapidSponsoredProductsHidden,
    rapidDeliveryBlockedSchemas,
    rapidDeliveryRulesLoaded,
  ])

  return { topSlot, rowSlots, isLoading }
}